/**
 * «Проекты» and one project — Spec 022, round-2 screens 5 and 6.
 *
 * A project is one card: how much is done, what waits for a check, what is
 * stuck, and the latest thing that happened. Inside, tasks are grouped by what
 * they need next, with a filter on top; a row shows a status only when there is
 * one, then the title, then who and what now. On a computer the task opens on
 * the right. The passport, resources and source ideas live one tap away
 * («О проекте»), not on top of the work.
 */

import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, FileText, Inbox, MoreHorizontal } from "lucide-react";

import { Areas, Overview, Projects, Tasks } from "@/api/resources";
import type { ActiveTask, OverviewOut, ProjectOut, ProjectStatusOut, TaskOut } from "@/api/types";
import { liveTaskQueryOptions } from "@/boot/query-sync";
import { INBOX_PROJECT_ID } from "@/lib/limits";
import { MemoryBlock } from "@/components/MemoryBlock";
import { Segmented } from "@/components/ui/Segmented";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { Mark, Status, type StatusTone } from "@/components/ui/Status";
import { AppBar, ago, Caption, Chip, EmptyNote, ListPane, Pill, ProgressLine, ScreenHeader, useIsDesktop, whoDid } from "@/kit";
import { cn } from "@/lib/cn";
import { useBackButton } from "@/lib/telegram-ui";
import { ClientsBlock, OrdersBoard } from "@/screens/OrdersScreen";
import { TaskScreen } from "@/screens/TaskScreen";

function useOverview() {
  return useQuery({ queryKey: ["overview"], queryFn: Overview.get, refetchInterval: 30_000 });
}

/** The newest active task of a project, as the card's «Последнее». */
function latestIn(overview: OverviewOut | undefined, title: string): ActiveTask | null {
  const mine = (overview?.active ?? []).filter((a) => a.item.project === title && (a.last_entry || a.last_activity_at));
  mine.sort((a, b) => (b.last_entry?.at ?? b.last_activity_at ?? "").localeCompare(a.last_entry?.at ?? a.last_activity_at ?? ""));
  return mine[0] ?? null;
}

function latestLine(a: ActiveTask): string {
  const e = a.last_entry;
  if (!e) return a.item.title;
  const what = e.current_stage || e.next_step || e.summary || a.item.title;
  return `${whoDid(e.author.name, e.via_agent)} · ${what}`;
}

/** Spec 052, step 6: «Проекты» holds the clients' orders and the clients as tabs of its own. */
type ProjectsTab = "projects" | "orders" | "clients";
const PROJECT_TABS: { value: ProjectsTab; label: string }[] = [
  { value: "projects", label: "Проекты" },
  { value: "orders", label: "Заказы" },
  { value: "clients", label: "Клиенты" },
];

export function ProjectsScreen() {
  const [params, setParams] = useSearchParams();
  const asked = params.get("tab");
  const tab: ProjectsTab = asked === "orders" || asked === "clients" ? asked : "projects";
  const setTab = (next: ProjectsTab) => {
    const query = new URLSearchParams(params);
    if (next === "projects") query.delete("tab"); else query.set("tab", next);
    setParams(query, { replace: true });
  };
  return (
    <div className="flex flex-col gap-3">
      <ScreenHeader title="Проекты" />
      <Segmented options={PROJECT_TABS} value={tab} onChange={setTab} className="max-w-[420px]" />
      {tab === "orders" ? <OrdersBoard /> : tab === "clients" ? <ClientsBlock /> : <ProjectsList />}
    </div>
  );
}

/** Spec 052, step 6: the projects grouped by direction, in the directions' own order. */
export function byDirection<P extends { area_id: string | null }>(
  projects: P[],
  areas: { id: string; name: string; order: number }[],
): { key: string; name: string; projects: P[] }[] {
  const groups = [...areas]
    .sort((a, b) => a.order - b.order)
    .map((a) => ({ key: a.id, name: a.name, projects: projects.filter((p) => p.area_id === a.id) }))
    .filter((g) => g.projects.length > 0);
  const known = new Set(areas.map((a) => a.id));
  const loose = projects.filter((p) => !p.area_id || !known.has(p.area_id));
  return loose.length ? [...groups, { key: "none", name: "Без направления", projects: loose }] : groups;
}

function ProjectsList() {
  const projects = useQuery({ queryKey: ["projects"], queryFn: () => Projects.list() });
  const areas = useQuery({ queryKey: ["areas", "with-archived"], queryFn: () => Areas.list(true) });
  const overview = useOverview();
  const ideasWaiting = (overview.data?.for_me ?? []).filter((w) => w.kind === "idea_review").length;
  // An empty «Входящие» is not a project to look at (PC audit, point 12).
  const list = (projects.data ?? []).filter((p) => !p.archived_at && !(p.id === INBOX_PROJECT_ID && p.task_count - p.tasks_done === 0));
  const groups = byDirection(list, areas.data ?? []);

  return (
    <div className="flex flex-col gap-3">
      {projects.isLoading && <SkeletonList count={3} />}
      {projects.isError && <EmptyNote>Не удалось загрузить проекты. Проверь связь.</EmptyNote>}
      {groups.map((g) => (
        <section key={g.key} className="flex flex-col gap-2">
          <Caption>{g.name}</Caption>
          <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
            {g.projects.map((p) => <ProjectCard key={p.id} project={p} overview={overview.data} />)}
          </div>
        </section>
      ))}
      <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
        <Link to="/ideas" className="flex flex-col gap-1.5 rounded-card border border-p-border bg-p-surface px-4 py-3.5 press-surface">
          <span className="flex items-center gap-2">
            <Inbox className="h-[18px] w-[18px] text-p-text-muted" aria-hidden="true" />
            <span className="flex-1 text-[17px] font-bold leading-6 text-p-text">Идеи</span>
            <span className="text-meta text-p-text-muted">твои и партнёра</span>
          </span>
          <span className="text-body text-p-text-muted">
            {ideasWaiting > 0 ? `${ideasWaiting} ${ideasWaiting === 1 ? "идея ждёт" : "идеи ждут"} твоего решения` : "Новых идей для тебя нет"}
          </span>
        </Link>
      </div>
      {!projects.isLoading && list.length === 0 && (
        <EmptyNote>Проектов пока нет. Запиши идею — партнёр превратит её в проект или задачу.</EmptyNote>
      )}
      <MemoryBlock />
    </div>
  );
}

function ProjectCard({ project: p, overview }: { project: ProjectOut; overview: OverviewOut | undefined }) {
  const summary = overview?.projects.find((s) => s.id === p.id);
  const mine = (overview?.active ?? []).filter((a) => a.item.project === p.title);
  const review = mine.filter((a) => a.flags.includes("result_pending")).length;
  // The same «в работе» as inside the project: the status says so, nothing waits or stands.
  const working = mine.filter((a) => a.status_category === "in_progress" && !a.flags.includes("result_pending") && !a.flags.includes("blocked")).length;
  const latest = latestIn(overview, p.title);
  return (
    <Link to={`/projects/${p.id}`} className="flex flex-col gap-2.5 rounded-card border border-p-border bg-p-surface px-4 py-3.5 press-surface">
      <span className="flex items-baseline gap-3">
        <span className="min-w-0 flex-1 truncate text-[17px] font-bold leading-6 text-p-text">{p.title}</span>
        <span className="shrink-0 text-meta text-p-text-muted">{p.tasks_done} из {p.task_count} сделано</span>
      </span>
      <ProgressLine done={p.tasks_done} total={p.task_count} />
      {(review > 0 || (summary?.blocked ?? 0) > 0 || working > 0) && (
        <span className="flex flex-wrap items-center gap-2">
          {review > 0 && <Pill tone="review">{review} ждёт проверки</Pill>}
          {(summary?.blocked ?? 0) > 0 && <Pill tone="waiting">{summary!.blocked} стоит</Pill>}
          {working > 0 && <span className="text-meta text-p-text-muted">{working} в работе</span>}
        </span>
      )}
      {latest && <span className="line-clamp-2 text-meta text-p-text-muted">Последнее: {latestLine(latest)}</span>}
    </Link>
  );
}

/* ── One project ──────────────────────────────────────────────────────── */

// PC audit 28.09: «в работе» means one thing everywhere — the status is «в работе»
// (point 11); a task with someone on it that has not started is «в очереди»;
// a cancelled task is not «сделано» (point 20).
type Group = "review" | "blocked" | "work" | "queue" | "free" | "done" | "cancelled";
const GROUPS: { key: Group; caption: string; chip: string }[] = [
  { key: "review", caption: "Ждут проверки", chip: "Проверить" },
  { key: "blocked", caption: "Стоят", chip: "Стоят" },
  { key: "work", caption: "В работе", chip: "В работе" },
  { key: "queue", caption: "В очереди", chip: "В очереди" },
  { key: "free", caption: "Можно взять", chip: "Взять" },
  { key: "done", caption: "Сделано", chip: "Сделано" },
  { key: "cancelled", caption: "Отменены", chip: "Отменены" },
];
const CLOSED: Group[] = ["done", "cancelled"];

interface Row {
  task: TaskOut;
  active: ActiveTask | undefined;
  group: Group;
}

export function groupOf(task: Pick<TaskOut, "completed_at" | "assignee_id" | "blocked_by">, active: Pick<ActiveTask, "flags"> | undefined, category: ProjectStatusOut["category"] | undefined): Group {
  if (category === "cancelled") return "cancelled";
  if (task.completed_at || category === "done") return "done";
  if (active?.flags.includes("result_pending")) return "review";
  if (active?.flags.includes("blocked") || (task.blocked_by?.length ?? 0) > 0) return "blocked";
  if (category === "in_progress") return "work";
  if (task.assignee_id) return "queue";
  return "free";
}

/** A journal stage says where the task stands only if written after its last status change. */
export function freshStage(active: Pick<ActiveTask, "last_entry" | "status_changed_at"> | undefined): string | null {
  const e = active?.last_entry;
  if (!e || !(e.current_stage || e.next_step)) return null;
  if (active?.status_changed_at && e.at && e.at < active.status_changed_at) return null;
  return e.current_stage || e.next_step || null;
}

export function ProjectScreenRoute() {
  useBackButton("/projects");
  const { project_id } = useParams<{ project_id: string }>();
  return project_id ? <ProjectScreen projectId={project_id} /> : null;
}

export function ProjectScreen({ projectId }: { projectId: string }) {
  const desktop = useIsDesktop();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [menu, setMenu] = useState(false);
  const project = useQuery({ queryKey: ["project", projectId], queryFn: () => Projects.get(projectId) });
  const statuses = useQuery({ queryKey: ["project-statuses", projectId], queryFn: () => Projects.statuses(projectId) });
  const tasks = useQuery({ queryKey: ["project-tasks", projectId], queryFn: () => Tasks.listInProject(projectId), ...liveTaskQueryOptions });
  const overview = useOverview();

  const activeById = new Map((overview.data?.active ?? []).map((a) => [a.item.id, a]));
  const categoryOf = new Map((statuses.data ?? []).map((s) => [s.id, s.category]));
  const rows: Row[] = (tasks.data ?? [])
    .filter((t) => !t.parent_task_id)
    .map((t) => {
      const active = activeById.get(t.id);
      return { task: t, active, group: groupOf(t, active, categoryOf.get(t.status_id)) };
    });
  const count = (g: Group) => rows.filter((r) => r.group === g).length;
  const open = rows.filter((r) => !CLOSED.includes(r.group)).length;

  const filter = (params.get("f") as Group | null) ?? null;
  const setFilter = (g: Group | null) => {
    const next = new URLSearchParams(params);
    if (g) next.set("f", g); else next.delete("f");
    setParams(next, { replace: true });
  };
  const selected = params.get("open");
  const openTask = (id: string) => {
    if (!desktop) return navigate(`/tasks/${id}`);
    const next = new URLSearchParams(params);
    next.set("open", id);
    setParams(next, { replace: true });
  };
  const closeTask = () => {
    const next = new URLSearchParams(params);
    next.delete("open");
    setParams(next, { replace: true });
  };

  const p = project.data;
  const list = (
    <div className="flex flex-col gap-3">
      <AppBar
        fallback="/projects"
        context="Проекты"
        trailing={
          <button type="button" aria-label="О проекте и другое" onClick={() => setMenu(true)}
            className="flex h-11 w-11 items-center justify-center rounded-full text-p-text press-surface">
            <MoreHorizontal className="h-6 w-6" aria-hidden="true" />
          </button>
        }
      />
      {project.isError && <EmptyNote>Проект не найден.</EmptyNote>}
      {p && (
        <ScreenHeader
          title={p.title}
          sub={<>{count("done")} из {rows.length - count("cancelled")} сделано · <Link to={`/projects/${p.id}/about`} className="text-p-link">паспорт проекта</Link></>}
        />
      )}
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] lg:mx-0 lg:flex-wrap lg:px-0 [&::-webkit-scrollbar]:hidden">
        <Chip on={filter == null} onClick={() => setFilter(null)}>Открытые <span className="font-medium">{open}</span></Chip>
        {GROUPS.filter((g) => count(g.key) > 0).map((g) => (
          <Chip key={g.key} on={filter === g.key} onClick={() => setFilter(filter === g.key ? null : g.key)}>
            {g.chip} <span className="font-medium">{count(g.key)}</span>
          </Chip>
        ))}
      </div>
      {tasks.isLoading && <SkeletonList count={4} />}
      {tasks.isError && <EmptyNote>Не удалось загрузить задачи проекта.</EmptyNote>}
      {!tasks.isLoading && rows.length === 0 && <EmptyNote>В проекте пока нет задач.</EmptyNote>}
      {GROUPS.filter((g) => (filter ? g.key === filter : !CLOSED.includes(g.key))).map((g) => {
        const items = rows.filter((r) => r.group === g.key);
        if (!items.length) return null;
        const shown = g.key === "done" ? [...items].sort((a, b) => (b.task.completed_at ?? "").localeCompare(a.task.completed_at ?? ""))
          : g.key === "cancelled" ? [...items].sort((a, b) => (b.task.updated_at ?? "").localeCompare(a.task.updated_at ?? ""))
          : items;
        return (
          <section key={g.key} className="flex flex-col gap-2">
            <Caption>{g.caption}</Caption>
            <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
              {shown.map((r) => <TaskRow key={r.task.id} row={r} selected={selected === r.task.id} onOpen={() => openTask(r.task.id)} />)}
            </ul>
          </section>
        );
      })}
      {!filter && (count("done") > 0 || count("cancelled") > 0) && (
        <span className="flex flex-wrap gap-x-5">
          {count("done") > 0 && (
            <button type="button" onClick={() => setFilter("done")} className="min-h-11 text-body font-semibold text-p-link">
              Показать сделанные ({count("done")})
            </button>
          )}
          {count("cancelled") > 0 && (
            <button type="button" onClick={() => setFilter("cancelled")} className="min-h-11 text-body font-semibold text-p-link">
              Показать отменённые ({count("cancelled")})
            </button>
          )}
        </span>
      )}

      <Sheet open={menu} onClose={() => setMenu(false)} title={p?.title ?? "Проект"}>
        <Link to={`/projects/${projectId}/about`} className="flex min-h-12 items-center gap-3 rounded-xl px-2 text-[15px] font-semibold text-p-text press-surface">
          <FileText className="h-5 w-5" aria-hidden="true" />
          <span className="flex-1">О проекте: паспорт, ресурсы, идеи</span>
          <ChevronRight className="h-5 w-5 text-p-text-muted" aria-hidden="true" />
        </Link>
      </Sheet>
    </div>
  );

  if (!desktop) return list;
  return (
    <ListPane
      list={list}
      listWidth={400}
      open={Boolean(selected)}
      onClose={closeTask}
      pane={selected ? <TaskScreen key={selected} taskId={selected} embedded /> : null}
      placeholder={rows.length ? <EmptyNote>Выбери задачу слева — она откроется здесь.</EmptyNote> : null}
    />
  );
}

function TaskRow({ row, selected, onOpen }: { row: Row; selected: boolean; onOpen: () => void }) {
  const { task: t, active: a, group } = row;
  // Spec 020 D3: «Сделано» only when a person confirmed the result (result_state, PR #21).
  const tone: StatusTone | null = group === "review" ? "review"
    : group === "blocked" ? ((a?.last_entry?.blocker ? "blocked" : "waiting") as StatusTone)
    : group === "done" && t.result_state === "confirmed" ? "done"
    : null;
  const mark = group === "cancelled" ? "отменена"
    : group !== "done" ? null
    : t.result_state === "skipped" || t.result_state === "pending" ? "закрыта без проверки"
    : t.result_state === "none" ? "закрыта без результата"
    : null;
  const waitingFor = a?.waiting_for?.[0];
  const who = t.assignee_name ?? a?.assignee?.name ?? null;
  const stage = freshStage(a);
  const line = group === "done" ? `${who ?? "без исполнителя"} · ${ago(t.completed_at)}`
    // A cancelled task has no completed_at: its last change is the cancellation.
    : group === "cancelled" ? `${who ?? "без исполнителя"} · отменена ${ago(t.updated_at)}`
    : a?.last_entry?.blocker ? `препятствие: ${a.last_entry.blocker}`
    : waitingFor ? `ждёт: ${waitingFor.item.title}`
    : stage && a?.last_entry
      ? `${who ?? whoDid(a.last_entry.author.name, a.last_entry.via_agent)} · ${stage}`
    : who ? `${who}${a?.last_activity_at ? ` · ${ago(a.last_activity_at)}` : ""}`
    : "свободна — можно взять";
  return (
    <li className="border-b border-p-border last:border-0">
      <button type="button" onClick={onOpen} className={cn("flex w-full items-start gap-3 px-4 py-3 text-left press-surface", selected && "bg-p-surface-2")}>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          {(tone || mark) && <span className="flex items-center gap-2">{tone && <Status tone={tone} />}{mark && <Mark>{mark}</Mark>}</span>}
          {/* Up to three lines in a list; the whole title is on the task (PC audit, point 17). */}
          <span title={t.title} className={cn("line-clamp-3 text-[15px] font-semibold leading-5", CLOSED.includes(group) ? "text-p-text-muted" : "text-p-text")}>{t.title}</span>
          <span className="line-clamp-2 text-meta text-p-text-muted">{line}</span>
        </span>
        <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />
      </button>
    </li>
  );
}
